import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import type { ListMeta } from '../../types/api';
import { Input } from './index';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  width?: string;
  hideOnMobile?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  meta?: ListMeta;
  page: number;
  onPage: (p: number) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  search?: string;
  onSearch?: (s: string) => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  emptyTitle?: string;
  emptyBody?: string;
}

/**
 * The one table. Every list page uses it, so pagination, loading, empty states and
 * mobile behaviour are decided once. Wide content scrolls inside the table, never the page.
 */
export function DataTable<T>({ columns, rows, meta, page, onPage, isLoading, isFetching, onRowClick, rowKey, search, onSearch, searchPlaceholder = 'Search…', filters, emptyTitle = 'Nothing here yet', emptyBody }: Props<T>) {
  return (
    <div className="surface overflow-hidden">
      {(onSearch || filters) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-4 py-3">
          {onSearch && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <Input value={search ?? ''} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} className="w-60 pl-9" />
            </div>
          )}
          {filters}
          {isFetching && !isLoading && <Loader2 className="ml-auto h-4 w-4 animate-spin text-bronze-400" />}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-[13px]">
          <thead>
            <tr className="border-b border-ink-100 bg-ink-50/70 text-left">
              {columns.map((c) => (
                <th key={c.key} className={`px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400 ${c.align === 'right' ? 'text-right' : ''} ${c.hideOnMobile ? 'hidden md:table-cell' : ''}`} style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-ink-100">{columns.map((c) => <td key={c.key} className="px-4 py-3.5"><div className="h-3 w-3/4 animate-pulse rounded bg-ink-100" /></td>)}</tr>
              ))
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-16 text-center"><p className="display text-[20px] text-ink-900">{emptyTitle}</p>{emptyBody && <p className="mt-1 text-[12.5px] text-ink-500">{emptyBody}</p>}</td></tr>
            ) : rows.map((row) => (
              <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} className={`border-b border-ink-100 transition last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-bronze-50/50' : ''}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-ink-700 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.hideOnMobile ? 'hidden md:table-cell' : ''}`}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50/40 px-4 py-2.5 text-[12px] text-ink-500">
          <span>{(page - 1) * meta.perPage + 1}–{Math.min(page * meta.perPage, meta.total)} of {meta.total}</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded-full p-1.5 transition hover:bg-white hover:text-ink-900 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <span className="px-2 tabular-nums">Page {page} of {meta.totalPages}</span>
            <button disabled={page >= meta.totalPages} onClick={() => onPage(page + 1)} className="rounded-full p-1.5 transition hover:bg-white hover:text-ink-900 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
